import type { GridCellParams, GridEventListener } from '@mui/x-data-grid-premium';
import { useEffect, useMemo } from 'react';
import type { GridApiRef } from '../DataGrid';
import { isColumnEditableForRow } from './cellEditability';

/**
 * A column lock on a cell MUI itself still treats as editable has to swallow the pointer, or a
 * row in edit mode opens the locked cell's editor. A cell the grid already closes needs no
 * guard, and swallowing its `mouseup` would keep MUI's document listener from moving focus,
 * leaving the previous cell to take the next keystrokes.
 */
const swallowsPointer = (params: GridCellParams): boolean =>
  params.isEditable === true && !isColumnEditableForRow(params.colDef, params.row);

export default function useColumnCellEditability(apiRef: GridApiRef): {
  handleCellDoubleClick: GridEventListener<'cellDoubleClick'>;
} {
  useEffect(() => {
    return apiRef.current?.subscribeEvent('cellMouseDown', (params, e) => {
      if (swallowsPointer(params)) {
        e.stopPropagation();
        e.preventDefault();
      }
    });
  }, [apiRef]);

  useEffect(() => {
    return apiRef.current?.subscribeEvent('cellMouseUp', (params, e) => {
      if (swallowsPointer(params)) {
        e.stopPropagation();
        e.preventDefault();
      }
    });
  }, [apiRef]);

  return useMemo(
    () => ({
      handleCellDoubleClick: (params, e) => {
        if (!isColumnEditableForRow(params.colDef, params.row)) {
          e.preventDefault();
          e.stopPropagation();
          e.defaultMuiPrevented = true;
        }
      },
    }),
    []
  );
}
